<template>
  <form class="vl-form" @submit.prevent="onSubmit">
    <vl-grid v-if="definition" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="definition">
          <vl-properties-label>{{ $t('deliveryprocessdefinition.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ definition.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('deliveryprocessdefinition.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ definition.label }}</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-column v-vl-spacer:bottom.small v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="duplicateProductProcessDefinitions"
            :label="$t('deliveryprocessdefinition.actions.duplicate.duplicateProductProcessDefinitions')"
          />
        </vl-form-column>
        <vl-form-column v-vl-spacer:bottom.small v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="duplicateDeliveryQualityChecks"
            :label="$t('deliveryprocessdefinition.actions.duplicate.duplicateDeliveryQualityChecks')"
          />
        </vl-form-column>
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving" :mod-loading="isSaving">{{
            $t('deliveryprocessdefinition.actions.duplicate.label')
          }}</vl-button>
          <vl-button
            v-focus
            :mod-disabled="isSaving"
            mod-icon-before
            mod-secondary
            tag-name="a"
            @click.prevent="cancel"
          >
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useDeliveryProcessDefinitionDetailStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-detail.store';
import { reactive } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useRouter } from 'vue-router';
import { useForm } from 'vee-validate';
import { boolean, object } from 'yup';
import PbsRadioButtonField from '@/components/forms/PbsRadioButtonField.vue';
import { parseException } from '@/modules/core/store/alert.store';

const router = useRouter();
const detailStore = useDeliveryProcessDefinitionDetailStore();
const props = defineProps({
  id: {
    type: Number,
    required: true
  }
});

const validationSchema = object({
  duplicateProductProcessDefinitions: boolean(),
  duplicateDeliveryQualityChecks: boolean()
});

const { handleSubmit, resetForm, values } = useForm({ validationSchema });

const data = reactive({
  definition: null,
  isLoading: false,
  isSaving: false,
  error: null
});

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadDeliveryProcessDefinition();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }

  setInitialFormValues();
});

const setInitialFormValues = () => {
  resetForm({
    values: {
      duplicateProductProcessDefinitions: false,
      duplicateDeliveryQualityChecks: false
    }
  });
};

const loadDeliveryProcessDefinition = async () => {
  const deliveryProcessDefinition = await detailStore.getDetail(props.id);
  if (deliveryProcessDefinition) {
    data.definition = deliveryProcessDefinition;
  }
};

const definition = computed(() => {
  return data.definition;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const error = computed(() => {
  return data.error;
});

const duplicate = async ({
  duplicateProductProcessDefinitions,
  duplicateDeliveryQualityChecks
}: {
  duplicateProductProcessDefinitions: boolean;
  duplicateDeliveryQualityChecks: boolean;
}): Promise<number> => {
  let id = null;
  try {
    data.isSaving = true;
    id = await detailStore.duplicate(props.id, {
      duplicateProductProcessDefinitions,
      duplicateDeliveryQualityChecks
    });
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
  return id;
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onSuccess = async (values) => {
  const id = await duplicate({
    duplicateProductProcessDefinitions: values.duplicateProductProcessDefinitions,
    duplicateDeliveryQualityChecks: values.duplicateDeliveryQualityChecks
  });

  closeModal();

  if (id) {
    router.push({
      name: 'deliveryprocessdefinition.detail',
      params: {
        id
      }
    });
  }
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>
<style lang="scss" scoped></style>
